import axios from 'axios';

const API_BASE_URL = process.env.VUE_APP_API_URL || '/api';
const CACHE_VERSION = '1.9'; // Agregar versión de caché

// Limpiar caché antiguo si es necesario
const initializeCache = () => {
    const storedVersion = localStorage.getItem('cacheVersion');
    if (storedVersion !== CACHE_VERSION) {
        console.log('Nueva versión de caché detectada, limpiando caché antiguo...');
        // Agregar limpieza de caché de actividades
        Object.keys(localStorage).forEach(key => {
            if (key.startsWith('activities_') || key === 'activity_stats') {
                localStorage.removeItem(key);
                localStorage.removeItem(`${key}_timestamp`);
            }
        });
        // Limpiar todo el caché relacionado
        localStorage.removeItem('accessToken');
        localStorage.removeItem('products');
        localStorage.removeItem('productsTimestamp');
        localStorage.removeItem('userOrders');
        localStorage.removeItem('adminOrders');
        localStorage.removeItem('ordersTimestamp');
        localStorage.removeItem('userPermissions');
        localStorage.removeItem('userPermissionsTimestamp');
        // Establecer nueva versión
        localStorage.setItem('cacheVersion', CACHE_VERSION);
    }
};

// Ejecutar limpieza al iniciar
initializeCache();

// Retry helper function
const axiosRetry = (axios, options = {}) => {
    const { 
        retries = 1, 
        retryDelay = 1000,
        retryStatus = [408, 429, 500, 502, 503, 504],
        shouldRetry = (error) => {
            const status = error.response ? error.response.status : null;
            return retryStatus.includes(status);
        }
    } = options;

    axios.interceptors.response.use(null, async (error) => {
        // Don't retry if we've maxed out our retries, or if this request explicitly doesn't want retries
        const config = error.config || {};
        if (!config || config.retry === 0 || !shouldRetry(error)) {
            return Promise.reject(error);
        }

        // Set the retry count
        config.retry = config.retry || 0;
        if (config.retry >= retries) {
            return Promise.reject(error);
        }
        config.retry++;

        // Calculate delay
        const delay = typeof retryDelay === 'function' 
            ? retryDelay(config.retry) 
            : retryDelay;
            
        console.warn(`Retrying request to ${config.url} (attempt ${config.retry}/${retries})`);
        
        // Wait for the delay
        await new Promise(resolve => setTimeout(resolve, delay));
        
        // Create new abort controller if needed
        if (config.signal && config.signal.aborted) {
            const controller = new AbortController();
            config.signal = controller.signal;
        }
        
        // Retry the request
        return axios(config);
    });
};

const apiClient = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
    },
    timeout: 30000, // Increased global timeout for all requests
});

// Apply retry functionality to apiClient
axiosRetry(apiClient, {
    retries: 2, // Increased from 1 to 2 retries for all endpoints
    retryDelay: (retryCount) => retryCount * 2000, // Progressive delay: 2s, 4s
    retryStatus: [408, 429, 500, 502, 503, 504]
});

// Axios 1.x usa AxiosHeaders; borrar solo la propiedad JS no borra Content-Type bien.
function stripMultipartContentType(config) {
    if (typeof FormData === 'undefined' || !(config.data instanceof FormData)) return;
    const h = config.headers;
    if (!h) return;
    if (typeof h.delete === 'function') {
        h.delete('Content-Type');
        h.delete('content-type');
        return;
    }
    delete h['Content-Type'];
    delete h['content-type'];
}

// Interceptor para agregar el token a todas las peticiones y configurar opciones especiales
apiClient.interceptors.request.use(config => {
    const token = localStorage.getItem('accessToken');
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }

    stripMultipartContentType(config);

    // Special handling for password reset endpoints - increased timeout and retries
    if (config.url && (
        config.url.includes('/auth/register/') ||
        config.url.includes('/auth/resend-verification/') ||
        config.url.includes('/auth/request-password-reset/') ||
        config.url.includes('/auth/verify-password-reset/') ||
        config.url.includes('/auth/resend-password-reset/')
    )) {
        console.log('Applying special config for auth email endpoint:', config.url);
        config.timeout = 60000; // 60 seconds timeout for email-related auth requests
        config.retry = 0; // Reset retry counter for these endpoints
        config.maxRetries = 3; // More retries for password reset
    }
    
    // Special handling for brand discount endpoint - increased timeout
    if (config.url && config.url.includes('/apply-discount')) {
        console.log('Applying special config for brand discount endpoint:', config.url);
        config.timeout = 45000; // 45 seconds timeout for discount operations
    }
    
    return config;
});

// ============================================
// SECURITY: Sistema de refresh automático de tokens
// ============================================
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
    failedQueue.forEach(prom => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });
    failedQueue = [];
};

apiClient.interceptors.response.use(
    response => response,
    async error => {
        const originalRequest = error.config;

        // Si es un 401 y no es un retry
        if (error.response?.status === 401 && !originalRequest._retry) {
            // Si es el endpoint de refresh, hacer logout directamente
            if (originalRequest.url && originalRequest.url.includes('/token/refresh/')) {
                import('@/services/auth').then(auth => {
                    auth.logout();
                    import('@/router').then(router => {
                        router.default.push({ 
                            name: 'LoginForm',
                            query: { 
                                redirect: router.default.currentRoute.value.fullPath,
                                message: 'Tu sesión ha expirado. Por favor, inicia sesión nuevamente.'
                            }
                        });
                    });
                });
                return Promise.reject(error);
            }

            if (isRefreshing) {
                // Si ya estamos refrescando, agregar a la cola
                return new Promise((resolve, reject) => {
                    failedQueue.push({ resolve, reject });
                }).then(token => {
                    originalRequest.headers.Authorization = `Bearer ${token}`;
                    return apiClient(originalRequest);
                }).catch(err => Promise.reject(err));
            }

            originalRequest._retry = true;
            isRefreshing = true;

            const refreshToken = localStorage.getItem('refreshToken');
            
            if (!refreshToken) {
                isRefreshing = false;
                import('@/services/auth').then(auth => {
                    auth.logout();
                    import('@/router').then(router => {
                        if (router.default.currentRoute.value.name !== 'LoginForm') {
                            router.default.push({ 
                                name: 'LoginForm',
                                query: { redirect: router.default.currentRoute.value.fullPath }
                            });
                        }
                    });
                });
                return Promise.reject(error);
            }

            try {
                // Usar axios directamente para evitar interceptores
                const response = await axios.post(`${API_BASE_URL}/auth/token/refresh/`, {
                    refresh: refreshToken
                });

                const { access, refresh: newRefresh } = response.data;
                
                localStorage.setItem('accessToken', access);
                if (newRefresh) {
                    localStorage.setItem('refreshToken', newRefresh);
                }

                apiClient.defaults.headers.Authorization = `Bearer ${access}`;
                originalRequest.headers.Authorization = `Bearer ${access}`;

                processQueue(null, access);
                
                return apiClient(originalRequest);

            } catch (refreshError) {
                processQueue(refreshError, null);
                
                import('@/services/auth').then(auth => {
                    auth.logout();
                    import('@/router').then(router => {
                        router.default.push({ 
                            name: 'LoginForm',
                            query: { 
                                redirect: router.default.currentRoute.value.fullPath,
                                message: 'Tu sesión ha expirado. Por favor, inicia sesión nuevamente.'
                            }
                        });
                    });
                });
                
                return Promise.reject(refreshError);
            } finally {
                isRefreshing = false;
            }
        }

        return Promise.reject(error);
    }
);

export default apiClient;